import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, GripVertical, Search } from "lucide-react";
import { toast } from "sonner";
import FileUploader from "@/components/admin/FileUploader";
import MultiImageUploader from "@/components/admin/MultiImageUploader";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableRow = ({ id, children, sortable }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !sortable });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? "var(--muted)" : undefined,
  };
  return (
    <tr ref={setNodeRef} style={style} className="border-t border-border">
      {sortable && (
        <td className="px-3 py-3 w-8">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
            aria-label="Drag to reorder"
            data-testid={`drag-handle-${id}`}
          >
            <GripVertical size={16} />
          </button>
        </td>
      )}
      {children}
    </tr>
  );
};

/**
 * Generic CRUD admin page.
 *
 * Props:
 * - title, description, endpoint, fields, listColumns, defaultValues, testKey (existing)
 * - sortable: boolean — when true, renders a drag-handle column and PUTs the new order
 *             via `{endpoint}/_reorder`. Items must have an `order` field.
 * - searchable: boolean — when true, renders a search input that filters by any listColumn value.
 */
const CrudPage = ({
  title,
  description,
  endpoint,
  fields,
  listColumns,
  defaultValues,
  testKey,
  sortable = false,
  searchable = true,
  reorderCollection,
}) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultValues);
  const [q, setQ] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = () => api.get(endpoint).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [endpoint]);

  const startCreate = () => {
    setEditing(null);
    setForm(defaultValues);
    setOpen(true);
  };
  const startEdit = (it) => {
    setEditing(it);
    setForm({ ...defaultValues, ...it });
    setOpen(true);
  };
  const save = async () => {
    try {
      const payload = { ...form };
      if ("order" in payload && payload.order !== "") payload.order = Number(payload.order) || 0;
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        if (sortable && payload.order === 0 && items.length > 0) {
          // Auto-assign next order
          payload.order = (Math.max(...items.map((i) => i.order || 0)) || 0) + 1;
        }
        await api.post(endpoint, payload);
        toast.success("Created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const remove = async (it) => {
    if (!window.confirm(`Delete "${it[listColumns[0].key]}"?`)) return;
    await api.delete(`${endpoint}/${it.id}`);
    toast.success("Deleted");
    load();
  };

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    const reordered = arrayMove(items, oldIdx, newIdx);
    const withOrder = reordered.map((it, idx) => ({ ...it, order: idx + 1 }));
    setItems(withOrder);
    try {
      // endpoint is like "/officials" → call /reorder/officials.
      // For collections whose URL prefix differs from collection name (e.g. banner → banner_items)
      // pass the explicit reorderCollection prop.
      const coll = reorderCollection || endpoint.replace(/^\//, "");
      await api.put(`/reorder/${coll}`, {
        items: withOrder.map((it) => ({ id: it.id, order: it.order })),
      });
      toast.success("Order saved");
    } catch (e) {
      toast.error("Could not save new order");
      load();
    }
  };

  const filtered = !q
    ? items
    : items.filter((it) =>
        listColumns.some((c) => String(it[c.key] ?? "").toLowerCase().includes(q.toLowerCase())),
      );

  const renderField = (f) => {
    if (f.options) {
      return (
        <select
          id={`f-${f.name}`}
          value={form[f.name] ?? ""}
          onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
          className="mt-1.5 w-full rounded-md border border-border bg-white px-3 py-2 text-sm"
          data-testid={`admin-${testKey}-field-${f.name}`}
        >
          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (f.type === "boolean") {
      return (
        <label className="flex items-center gap-2 mt-2 text-sm">
          <input
            type="checkbox"
            checked={!!form[f.name]}
            onChange={(e) => setForm({ ...form, [f.name]: e.target.checked })}
            data-testid={`admin-${testKey}-field-${f.name}`}
          />
          {f.placeholder || f.label}
        </label>
      );
    }
    if (f.type === "file_upload") {
      return (
        <div className="mt-2">
          <FileUploader
            kind={f.fileKind || "pdf"}
            value={form[f.name] ?? ""}
            onChange={(id) => setForm({ ...form, [f.name]: id })}
            testKey={`admin-${testKey}-upload-${f.name}`}
          />
        </div>
      );
    }    if (f.type === "multi_image") {
      return (
        <div className="mt-2">
          <MultiImageUploader
            value={form[f.name] || []}
            onChange={(ids) => setForm({ ...form, [f.name]: ids })}
            testKey={`admin-${testKey}-multi-${f.name}`}
          />
        </div>
      );
    }
    if (f.multiline) {
      return (
        <Textarea
          id={`f-${f.name}`}
          rows={f.rows || 5}
          value={form[f.name] ?? ""}
          onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
          placeholder={f.placeholder}
          className="mt-1.5"
          data-testid={`admin-${testKey}-field-${f.name}`}
        />
      );
    }
    return (
      <Input
        id={`f-${f.name}`}
        type={f.type || "text"}
        value={form[f.name] ?? ""}
        onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
        placeholder={f.placeholder}
        className="mt-1.5"
        data-testid={`admin-${testKey}-field-${f.name}`}
      />
    );
  };

  return (
    <div data-testid={`admin-${testKey}-page`}>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">{title}</h1>
          {description && <p className="text-muted-foreground mt-1">{description}</p>}
          {sortable && <p className="text-xs text-muted-foreground mt-1">Drag the handle on the left of any row to reorder.</p>}
        </div>
        <div className="flex items-center gap-2">
          {searchable && items.length > 3 && (
            <div className="relative w-56">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search..."
                className="pl-9 h-9"
                data-testid={`admin-${testKey}-search`}
              />
            </div>
          )}
          <Button onClick={startCreate} className="bg-primary hover:bg-primary/90" data-testid={`admin-${testKey}-add-btn`}>
            <Plus size={16} className="mr-1.5" /> Add new
          </Button>
        </div>
      </div>

      <Card className="border-border">
        <CardContent className="p-0">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  {sortable && <th className="w-8" />}
                  {listColumns.map((c) => (
                    <th key={c.key} className="text-left px-5 py-3 font-medium text-foreground/80">{c.label}</th>
                  ))}
                  <th className="px-5 py-3 w-32" />
                </tr>
              </thead>
              <SortableContext items={filtered.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={listColumns.length + (sortable ? 2 : 1)} className="px-5 py-10 text-center text-muted-foreground">
                      {q ? "No matches." : "No items yet."}
                    </td></tr>
                  )}
                  {filtered.map((it) => (
                    <SortableRow key={it.id} id={it.id} sortable={sortable && !q}>
                      {listColumns.map((c) => (
                        <td key={c.key} className="px-5 py-3 align-top" data-testid={`admin-${testKey}-row-${it.id}`}>
                          <div className="max-w-md truncate">{String(it[c.key] ?? "")}</div>
                        </td>
                      ))}
                      <td className="px-5 py-3 text-right">
                        <Button variant="ghost" size="icon" onClick={() => startEdit(it)} data-testid={`admin-${testKey}-edit-${it.id}`} aria-label="Edit">
                          <Pencil size={15} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(it)} data-testid={`admin-${testKey}-delete-${it.id}`} aria-label="Delete">
                          <Trash2 size={15} />
                        </Button>
                      </td>
                    </SortableRow>
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit" : "New"} {title.replace(/s$/, "")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {fields.map((f) => (
              <div key={f.name}>
                <Label htmlFor={`f-${f.name}`}>{f.label}</Label>
                {f.hint && <p className="text-xs text-muted-foreground mt-0.5 mb-1">{f.hint}</p>}
                {renderField(f)}
              </div>
            ))}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={`admin-${testKey}-cancel-btn`}>Cancel</Button>
            <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid={`admin-${testKey}-save-btn`}>
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrudPage;
